import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Wifi, Smartphone, Volume2, Shield, Globe, Zap } from "lucide-react";
import { MARKETING_CONFIG } from "@/config/marketing";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { Logo } from "@/components/ui/Logo";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";

const FEATURES = [
  { icon: Volume2, title: "HD Voice", desc: "Wideband audio on the elefa-to-server leg delivers clear, natural sound. Final quality may vary depending on the destination network and handset." },
  { icon: Wifi, title: "Works on Any Network", desc: "WiFi, 4G, or 5G. As long as you have internet, you can call." },
  { icon: Smartphone, title: "Any Device", desc: "Use your phone, tablet, or computer. No special hardware needed." },
  { icon: Shield, title: "Encrypted in Transit", desc: "All calls and signalling between your device and our servers are encrypted in transit (TLS, SRTP). Calls terminate on the public phone network, which by its nature cannot be end-to-end encrypted." },
  { icon: Globe, title: "Every Country Worldwide", desc: "Call landlines and mobiles worldwide with the same great quality." },
  { icon: Zap, title: "Low Latency", desc: "Optimised routing keeps conversations natural with minimal delay." },
];

export default function HDCalling() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={PAGE_SEO.hdCalling.title}
        description={PAGE_SEO.hdCalling.description}
        keywords={PAGE_SEO.hdCalling.keywords}
        canonicalUrl={getCanonicalUrl("/hd-calling")}
        structuredData={getBreadcrumbSchema([
          { name: "Home", url: "https://elefa.tel/" },
          { name: "HD Calling", url: "https://elefa.tel/hd-calling" },
        ])}
      />

      <nav className="container py-4 sm:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/connect-portal/auth"><Button variant="ghost" size="sm">Log In</Button></Link>
          <Link to="/connect-portal/auth"><Button size="sm">Get Started</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="secondary"><Volume2 className="h-3 w-3 mr-1 inline" /> HD Voice</Badge>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight">
            Clear calls to any phone.<br />No app on their end.
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            elefa routes your call over the internet and connects to any phone worldwide. The person you call just needs a phone number. Nothing else.
          </p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2 mt-4">Try HD Calling Free <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Built for Quality</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-background rounded-lg border p-6 space-y-3">
                <f.icon className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-semibold">{f.title}</h3>
                <p className="text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How VoIP Works */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">How It Works</h2>
            <p className="text-muted-foreground">
              elefa Connect uses VoIP (Voice over Internet Protocol) to route your calls over the internet instead of traditional phone lines. This means:
            </p>
            <div className="grid sm:grid-cols-3 gap-6 text-center pt-4">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">HD</div>
                <p className="text-sm text-muted-foreground">Wideband audio codecs for natural clarity</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">70%</div>
                <p className="text-sm text-muted-foreground">Cheaper than traditional carriers</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">Worldwide</div>
                <p className="text-sm text-muted-foreground">Every country reachable from any device</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary/5 border-t">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Hear the Difference?</h2>
          <p className="text-lg text-muted-foreground mb-8">Sign up free and make your first HD call in under a minute.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/connect-portal/auth">
              <Button size="lg" className="gap-2">Get Started Free <ArrowRight className="h-4 w-4" /></Button>
            </Link>
            <Link to="/connect-portal/rates">
              <Button variant="outline" size="lg">View Rates</Button>
            </Link>
          </div>
        </div>
      </section>

      <ConnectFooter />
    </div>
  );
}
